import { FormikProps } from "formik"
import React, { useEffect } from "react"
import { FormGroup, Col, Label, Input, FormFeedback, Row } from "reactstrap"
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store";
import { genders } from "constants/campaign";
import { RequirementDetails } from "types/campaign/requirement";
import AsyncSelect from 'react-select/async';
import { fetchCities, fetchTopics } from "services/api/campaign";
import { fetchRegionsList } from "slices/master/thunk";
import InfluencerIcon from "assets/icons/InfluencerIcon";
import BirthdayIcon from "assets/icons/BirthdayIcon";
import LocationIcon from "assets/icons/LocationIcon";
import NichesIcon from "assets/icons/NichesIcon";
import FollowersIcon from "assets/icons/FollowersIcon";
import RadioGroup from "Components/input/RadioGroup/RadioGroup";
import LanguagesIcon from "assets/icons/LanguagesIcon";
import { debounce } from "lodash";
import FieldInstruction from "Components/Common/FieldInstruction";
import { FIELDS_INSTRUCTIONS } from "constants/fieldsInstructions";
import CustomMultiSelect from "Components/Common/CustomMultiSelect";
import CustomAsyncMultiSelect from "Components/Common/CustomAsyncMultiSelect";
import { isset } from "utils/helpers";

const applicationRestrictionOptions = {
    allow: 'Allow All',
    restrict_ongoing_collab: 'Restrict Ongoing Collab',
    restrict_completed_collab: 'Restrict Any Collab'
}

const InfluencerCrieteria: React.FC<{ formFields: FormikProps<RequirementDetails> }> = ({ formFields }) => {

    const niches = useSelector((state: RootState) => state.auth.master?.niche_list);

    const regions = useSelector((state: RootState) => state.master.regions.value);

    const age_groups = useSelector((state: RootState) => state.auth.master?.age_group_list);

    const languages = useSelector((state: RootState) => state.master.languages);


    const dispatch: AppDispatch = useDispatch();

    const _loadCitiesSuggestions = (query: string, callback: any) => {
        if (!query || query.length < 3) callback([]);

        fetchCities(query)
            .then(resp => callback(resp.map((item: string) => ({ value: item, label: item })))).catch(() => callback([]))
    };

    const loadCitiesSuggestions = debounce(_loadCitiesSuggestions, 200);

    const _loadTopicsSuggestions = (query: string, callback: any) => {
        if (!query || query.length < 3) callback([]);

        fetchTopics(query)
            .then(resp => callback(resp.map((item: string) => ({ value: item, label: item })))).catch(() => callback([]))
    };

    const loadTopicsSuggestions = debounce(_loadTopicsSuggestions, 200);


    useEffect(() => {
        dispatch(fetchRegionsList());
    }, [dispatch])

    return (
        <React.Fragment>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><NichesIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Exclude creators from past campaign(s)
                                <span className="text-danger"> *</span>
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.APPLICATION_RESTRICTION} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1 align-self-center'>
                    <RadioGroup
                        name="application_restriction"
                        options={Object.entries(applicationRestrictionOptions).map(([key, value]) => ({ id: key, label: value }))}
                        defaultValue={formFields?.values?.application_restriction ?? 'allow'}
                        onChange={(value) => {
                            formFields.setValues({
                                ...formFields.values,
                                application_restriction: value,
                                ...(value !== 'restrict_completed_collab' ? { application_restriction_duration: undefined } : {})
                            });
                        }}
                    />
                </Col>
            </FormGroup>
            <div className="divider"></div>
            {
                formFields.values?.application_restriction === 'restrict_completed_collab' &&
                <>

                    <FormGroup row className='py-4 mt-3'>
                        <Col sm={4} className='my-1'>
                            <div className="d-flex gap-3">
                                <div className="w-40px">
                                    <span className="icon-circle me-3"><NichesIcon /></span>
                                </div>
                                <div className="d-flex flex-column gap-2">
                                    <Label htmlFor='name' className='form-label input-label mb-0'>
                                        Application Restriction Duration
                                        <span className="text-danger"> *</span>
                                    </Label>
                                    <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.APPLICATION_RESTRICTION_DURATION} />
                                </div>
                            </div>
                        </Col>
                        <Col sm={8} className='my-1 align-self-center'>
                            <select value={(isset(formFields.values.application_restriction_duration)) ? formFields.values.application_restriction_duration : 2}
                                onChange={(e: any) => {
                                    formFields.setFieldValue('application_restriction_duration', !isNaN(Number(e.target.value)) ? Number(e.target.value) : 2);
                                }}
                                className="form-select w-50">
                                <option value="2">2 Weeks</option>
                                <option value="4">4 Weeks</option>
                                <option value="8">8 Weeks</option>
                                <option value="12">12 Weeks</option>
                                <option value="0">Forever</option>
                            </select>
                        </Col>
                    </FormGroup>
                    <div className="divider"></div>
                </>
            }
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><InfluencerIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Influencer's Gender
                                <span className="text-danger"> *</span>
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.INFLUENCER_GENDER} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1 align-self-center'>
                    <RadioGroup
                        name="gender"
                        options={Object.entries(genders).map(([key, value]) => ({ id: key, label: value }))}
                        defaultValue={formFields?.values?.gender ?? 'A'}
                        onChange={(gender) => {
                            formFields.setFieldValue('gender', gender)
                        }}
                    />
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><BirthdayIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Influencer's Age
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.INFLUENCER_AGE} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <div className="d-flex gap-3">
                        <select className="form-select w-25"
                            value={formFields?.values?.age?.min ?? ''}
                            onChange={(e) => formFields.setFieldValue('age', { ...formFields.values.age, min: e.target.value })}
                            onBlur={() => formFields.setFieldTouched('age', true, true)}
                            name="age-min"
                        >
                            <option value="">From</option>
                            {Array.from({ length: 87 }, (_, i) => i + 13).map((age) => {
                                if (formFields?.values?.age?.max && age > formFields.values.age.max) return null;
                                return (
                                    <option key={age} value={age}>
                                        {age}
                                    </option>
                                )
                            })}
                        </select>
                        <select className="form-select w-25"
                            value={formFields?.values?.age?.max ?? ''}
                            onChange={(e) => formFields.setFieldValue('age', { ...formFields.values.age, max: e.target.value })}
                            onBlur={() => formFields.setFieldTouched('age', true, true)}
                            name="age-max"
                        >
                            <option value="">To</option>
                            {Array.from({ length: 87 }, (_, i) => i + 13).map((age) => {
                                if (formFields?.values?.age?.min && age < formFields.values.age.min) return null
                                return (
                                    <option key={age} value={age}>
                                        {age}
                                    </option>
                                )
                            })}
                        </select>
                    </div>
                    {
                        formFields.errors.age &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.age as string}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><LocationIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Influencer's Location(s)
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.INFLUENCER_LOCATION} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1 align-self-center'>
                    <Row>
                        <Col sm={3} className="d-flex">
                            <RadioGroup
                                name="location-type"
                                options={[{ id: 'city', label: 'City' }, { id: 'state', label: 'State' }]}
                                defaultValue={formFields?.values?.cities ? 'city' : 'state'}
                                onChange={(locationType) => {
                                    if (locationType === 'city') {
                                        formFields.setValues({ ...formFields.values, regions: null, cities: [] });
                                    } else {
                                        formFields.setValues({ ...formFields.values, regions: [], cities: null });
                                    }
                                }}
                            />
                        </Col>
                        <Col sm={9}>
                            {
                                formFields?.values?.cities &&
                                <CustomAsyncMultiSelect
                                    placeholder="Select Location"
                                    menuPlacement='top'
                                    cacheOptions
                                    isMulti
                                    loadOptions={loadCitiesSuggestions}
                                    styles={{
                                        multiValueLabel: (base: any) => {
                                            return { ...base, color: "white" }
                                        }
                                    }}
                                    values={formFields?.values?.cities ?? []}
                                    onChange={(selectedCities) => {
                                        formFields.setFieldValue('cities', selectedCities);
                                    }}
                                />
                            }
                            {
                                formFields?.values?.regions &&
                                <CustomMultiSelect
                                    placeholder="Select Location"
                                    menuPlacement='top'
                                    isMulti
                                    styles={{
                                        multiValueLabel: (base: any) => {
                                            return { ...base, color: "white" }
                                        }
                                    }}
                                    options={regions.map((region) => ({ label: region, value: region }))}
                                    values={formFields?.values?.regions ?? []}
                                    onChange={(selectedRegions) => {
                                        formFields.setFieldValue('regions', selectedRegions);
                                    }}
                                />
                            }
                        </Col>
                    </Row>
                    {
                        formFields.touched.cities && formFields.errors.cities &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.cities}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><NichesIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Niches
                                <span className="text-danger"> *</span>
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.NICHES} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <CustomMultiSelect
                        placeholder="Select Niches"
                        menuPlacement='top'
                        isMulti
                        styles={{
                            multiValueLabel: (base: any) => {
                                return { ...base, color: "white" }
                            }
                        }}
                        options={Object.entries((niches ?? {})).map(([id, name]) => ({ label: name, value: id }))}
                        classNamePrefix={'influns'}
                        values={formFields.values?.niches ?? []}
                        onChange={(selectedNiches) => {
                            formFields.setFieldValue('niches', selectedNiches);
                        }}
                    />
                    {
                        formFields.touched.niches && formFields.errors.niches &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.niches}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><NichesIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Topics
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.TOPICS} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <CustomAsyncMultiSelect
                        placeholder="Select Topics"
                        menuPlacement='top'
                        cacheOptions
                        isMulti
                        loadOptions={loadTopicsSuggestions}
                        styles={{
                            multiValueLabel: (base: any) => {
                                return { ...base, color: "white" }
                            }
                        }}
                        values={formFields?.values?.topics ?? []}
                        onChange={(selectedTopics) => {
                            formFields.setFieldValue('topics', selectedTopics);
                        }}
                    />
                    {
                        formFields.touched.topics && formFields.errors.topics &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.topics}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><NichesIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Exclude Topics
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.EXCLUDE_TOPICS} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <CustomAsyncMultiSelect
                        placeholder="Select Topics To Exclude"
                        menuPlacement='top'
                        cacheOptions
                        isMulti
                        loadOptions={loadTopicsSuggestions}
                        styles={{
                            multiValueLabel: (base: any) => {
                                return { ...base, color: "white" }
                            }
                        }}
                        values={formFields?.values?.exclude_topics ?? []}
                        onChange={(selectedTopics) => {
                            formFields.setFieldValue('exclude_topics', selectedTopics);
                        }}
                    />
                    {
                        formFields.touched.topics && formFields.errors.exclude_topics &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.topics}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><LanguagesIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Influencer's Language
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.INFLUENCER_LANGUAGE} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <CustomMultiSelect
                        placeholder="Select Language"
                        menuPlacement='top'
                        isMulti
                        styles={{
                            multiValueLabel: (base: any) => {
                                return { ...base, color: "white" }
                            }
                        }}
                        options={Object.entries((languages ?? {})).map(([id, name]) => ({ label: name, value: id }))}
                        values={formFields?.values?.languages ?? []}
                        onChange={(selectedLang) => {
                            formFields.setFieldValue('languages', selectedLang);
                        }}
                    />
                    {
                        formFields.touched.languages && formFields.errors.languages &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.languages}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><FollowersIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='follower_count' className='form-label input-label mb-0'>
                                Minimum Instagram Followers
                                <span className="text-danger"> *</span>
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.MIN_INSTAGRAM_FOLLOWERS} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <Input
                        type='text'
                        name='follower_count'
                        className='form-control'
                        onChange={(e) => formFields.setFieldValue('follower_count', { min: parseInt(e.target.value ? e.target.value : '0') })}
                        onBlur={() => formFields.setFieldTouched('follower_count', true, true)}
                        value={formFields?.values?.follower_count?.min ? formFields.values.follower_count.min : ''}
                        invalid={formFields.touched.follower_count?.min && formFields.errors.follower_count?.min ? true : false}
                        placeholder='Minimum Instagram Followers' />
                    {
                        formFields.touched.follower_count && formFields.errors.follower_count?.min &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.follower_count.min}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><FollowersIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='average_views' className='form-label input-label mb-0'>
                                Minimum Average Views
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.MIN_AVERAGE_VIEWS} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <Input
                        type='text'
                        name='average_views'
                        className='form-control'
                        onChange={(e) => formFields.setFieldValue('average_views', { min: parseInt(e.target.value ? e.target.value : '0') })}
                        onBlur={() => formFields.setFieldTouched('average_views', true, true)}
                        value={formFields?.values?.average_views?.min ? formFields.values.average_views.min : ''}
                        invalid={formFields.touched.average_views?.min && formFields.errors.average_views?.min ? true : false}
                        placeholder='Minimum Average Views' />
                    {
                        formFields.touched.average_views && formFields.errors.average_views?.min &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.average_views.min}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><InfluencerIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='audience_gender' className='form-label input-label mb-0'>
                                Audience's Gender
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.AUDIENCE_GENDER} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <RadioGroup
                        name="audience_gender"
                        options={Object.entries(genders).map(([key, value]) => ({ id: key, label: value }))}
                        defaultValue={formFields?.values?.audience_gender ?? 'A'}
                        onChange={(audience_gender) => {
                            formFields.setFieldValue('audience_gender', audience_gender)
                        }}
                    />
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><BirthdayIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Audience Age
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.AUDIENCE_AGE} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <CustomMultiSelect
                        placeholder="Select Age Group"
                        menuPlacement='top'
                        isMulti
                        styles={{
                            multiValueLabel: (base: any) => {
                                return { ...base, color: "white" }
                            }
                        }}
                        options={(age_groups ?? []).map((age_group) => ({ label: age_group, value: age_group }))}
                        values={formFields?.values?.audience_age_groups ?? []}
                        onChange={(selectedAgeGroups) => {
                            formFields.setFieldValue('audience_age_groups', selectedAgeGroups);
                        }}
                    />
                </Col>
            </FormGroup>
            <div className="divider"></div>
            <FormGroup row className='py-4 mt-3'>
                <Col sm={4} className='my-1'>
                    <div className="d-flex gap-3">
                        <div className="w-40px">
                            <span className="icon-circle me-3"><LocationIcon /></span>
                        </div>
                        <div className="d-flex flex-column gap-2">
                            <Label htmlFor='name' className='form-label input-label mb-0'>
                                Audience's Location(s)
                            </Label>
                            <FieldInstruction instruction={FIELDS_INSTRUCTIONS.CAMPAIGN.AUDIENCE_LOCATION} />
                        </div>
                    </div>
                </Col>
                <Col sm={8} className='my-1'>
                    <CustomAsyncMultiSelect
                        placeholder="Choose one or more"
                        menuPlacement='top'
                        cacheOptions
                        isMulti
                        loadOptions={loadCitiesSuggestions}
                        styles={{
                            multiValueLabel: (base: any) => {
                                return { ...base, color: "white" }
                            }
                        }}
                        values={formFields?.values?.audience_cities ?? []}
                        onChange={(selectedCities) => {
                            formFields.setFieldValue('audience_cities', selectedCities);
                        }}
                    />
                    {
                        formFields.touched.audience_cities && formFields.errors.audience_cities &&
                        <FormFeedback type="invalid" className="d-block">
                            {formFields.errors.audience_cities}
                        </FormFeedback>
                    }
                </Col>
            </FormGroup>
        </React.Fragment>
    )
}

export default InfluencerCrieteria;